import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './BotAccessPage.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import axios from 'axios';
import { SETTINGS } from '../../shared/routes/path';
import Loader from '../../shared/components/common/loader/Loader';
import Title from '../../shared/components/title/Title';
import EmptyState from '../../shared/components/common/empty-state/EmptyState';
import EmptyPanel from '../../shared/components/empty-panel/EmptyPanel';
import CustomTooltip from '../../shared/components/common/tooltip/CustomTooltip';
import { useGetBotAccess } from '../../shared/api/hooks/bot-access/useGetBotAccess';
import { useRefreshBotAccess } from '../../shared/api/hooks/bot-access/useRefreshBotAccess';
import { useGetProject } from '../../shared/api/hooks/project/useGetProject';
import BotStatusTable from './components/BotStatusTable';
import { BotAccessAgent } from '../../shared/types/bot-access/BotAccessAgent';

const REFRESH_COOLDOWN_MS = 60 * 60 * 1000;
const TOAST_HIDE_MS = 5000;
const MIN_429_COOLDOWN_MS = 60 * 1000;

const parseProjectId = (value?: string): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getRobotsTxtUrl = (target: string | null | undefined): string | null => {
  if (!target) return null;
  const trimmed = target.trim();
  if (!trimmed) return null;
  const bare = trimmed
    .replace(/^https?:\/\//i, '')
    .replace(/[/?#].*$/, '')
    .trim();
  if (!bare) return null;
  try {
    return `${new URL(`https://${bare}`).origin}/robots.txt`;
  } catch {
    return `https://${bare}/robots.txt`;
  }
};

const BotAccessPage: React.FC = () => {
  const { t } = useTranslation();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = parseProjectId(projectIdParam);

  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetBotAccess(projectId);
  const { project, isLoading: projectLoading } = useGetProject(projectId);
  const refresh = useRefreshBotAccess(projectId ?? 0);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const agents: BotAccessAgent[] = useMemo(() => data?.agents ?? [], [data?.agents]);

  const lastCheckedAtMs = useMemo(() => {
    let max = 0;
    for (const a of agents) {
      const t = a.lastCheckedAt ? Date.parse(a.lastCheckedAt) : NaN;
      if (Number.isFinite(t) && t > max) max = t;
    }
    return max > 0 ? max : null;
  }, [agents]);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), TOAST_HIDE_MS);
    return () => window.clearTimeout(id);
  }, [toast]);

  const formatCooldown = useCallback(
    (ms: number): string => {
      const totalMinutes = Math.max(1, Math.ceil(ms / 60000));
      return t('botAccess.cooldownMinutes', { count: totalMinutes });
    },
    [t]
  );

  const handleRefresh = useCallback(() => {
    refresh.mutate(undefined, {
      onError: (err) => {
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 429) {
          const remaining =
            lastCheckedAtMs !== null
              ? Math.max(MIN_429_COOLDOWN_MS, lastCheckedAtMs + REFRESH_COOLDOWN_MS - Date.now())
              : REFRESH_COOLDOWN_MS;
          setToast(t('botAccess.cooldownToast', { time: formatCooldown(remaining) }));
        } else {
          setToast(t('botAccess.refreshError'));
        }
      },
    });
  }, [refresh, lastCheckedAtMs, t, formatCooldown]);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Auto-trigger first robots.txt check when entering an unchecked project.
  // The user expects the page to populate without an extra click. Guarded by a
  // ref so we never loop on errors and never re-fire after the user navigates
  // back to the page when data already exists. Backend 1h rate limit kicks in
  // for repeats — auto-trigger only runs while `agents` is empty (= never
  // checked), so it can't collide with the cooldown.
  const autoTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoTriggeredRef.current) return;
    if (isLoading || projectLoading || isError) return;
    if (refresh.isPending) return;
    if (agents.length > 0) return;
    if (!project || (!project.website && !project.domain)) return;
    autoTriggeredRef.current = true;
    handleRefresh();
  }, [
    isLoading,
    projectLoading,
    isError,
    refresh.isPending,
    agents.length,
    project,
    handleRefresh,
  ]);

  const stats = useMemo(() => {
    let open = 0;
    let blocked = 0;
    let partial = 0;
    for (const a of agents) {
      if (a.status === 'Allowed') open += 1;
      else if (a.status === 'Blocked') blocked += 1;
      else partial += 1;
    }
    return { open, blocked, partial, restrictions: blocked + partial };
  }, [agents]);

  const filteredAgents = useMemo(() => {
    if (!search.trim()) return agents;
    const q = search.trim().toLowerCase();
    return agents.filter(
      (a) =>
        (a.agentName ?? '').toLowerCase().includes(q) ||
        (a.platform ?? '').toLowerCase().includes(q) ||
        (a.category ?? '').toLowerCase().includes(q) ||
        a.status.toLowerCase().includes(q)
    );
  }, [agents, search]);

  const robotsTxtUrl = useMemo(
    () => getRobotsTxtUrl(project?.domain || project?.website || null),
    [project?.domain, project?.website]
  );

  if (isLoading || projectLoading) {
    return <Loader />;
  }

  if (project && !project.website && !project.domain) {
    return (
      <div className="bot-access-page page-content">
        <div className="page-box">
          <div className="page-box__filters" data-observe-sticky>
            <Title isBaseCategory={false} title={t('botAccess.title')} />
          </div>
          <div className="page-box__content">
            <main>
              <EmptyState
                mode="SMALL"
                emptyStateData={{
                  title: t('botAccess.title'),
                  content: t('botAccess.noWebsite'),
                  children: (
                    <button
                      className="btn btn--sm"
                      type="button"
                      onClick={() => navigate(SETTINGS)}
                    >
                      {t('botAccess.goToSettings')}
                    </button>
                  ),
                }}
              />
            </main>
          </div>
        </div>
      </div>
    );
  }

  const projectName = project?.firmNameOrg || project?.projectLabel || project?.firmName || '';

  const heroTitle = projectName
    ? t('botAccess.heroTitle', {
        project: projectName,
        restrictions: stats.restrictions,
        open: stats.open,
      })
    : '';

  return (
    <div className="bot-access-page page-content">
      <div className="page-box">
        <div className="page-box__filters" data-observe-sticky>
          <Title isBaseCategory={false} title={t('botAccess.title')}>
            <div className="filters">
              <button
                className="btn btn--sm"
                onClick={handleRefresh}
                type="button"
              >
                {refresh.isPending ? (
                  <span className="ico--loader ico--xs" aria-hidden="true" />
                ) : (
                  <span className="ico--refresh ico--xs" aria-hidden="true" />
                )}
                {t('botAccess.reloadRobots')}
              </button>
              <a
                className="btn btn--sm"
                href={robotsTxtUrl ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="ico--globe ico--xs" aria-hidden="true" />
                {t('botAccess.viewRobots')}
              </a>
            </div>
          </Title>
        </div>

        <div className="page-box__stats">
          {agents.length > 0 && (
            <header className="bot-access-page__hero">
              <div className="bot-access-page__hero-text">
                <h2 className="bot-access-page__hero-title">{heroTitle}</h2>
                <p className="bot-access-page__hero-subtitle">
                  {t('botAccess.heroSubtitle')}
                </p>
              </div>
              <div className="bot-access-page__hero-tile-row">
                <div className="bot-access-page__hero-tile">
                  <span className="bot-access-page__hero-tile-label">
                    <span className="bot-access-page__status-dot bot-access-page__status-dot--open" />
                    {t('botAccess.statusOpen')}
                    <CustomTooltip tooltipText="botAccess.tileOpenHint" />
                  </span>
                  <span className="bot-access-page__hero-tile-value">{stats.open}</span>
                </div>
                <div className="bot-access-page__hero-tile">
                  <span className="bot-access-page__hero-tile-label">
                    <span className="bot-access-page__status-dot bot-access-page__status-dot--partial" />
                    {t('botAccess.statusPartial')}
                    <CustomTooltip tooltipText="botAccess.tilePartialHint" />
                  </span>
                  <span className="bot-access-page__hero-tile-value">{stats.partial}</span>
                </div>
                <div className="bot-access-page__hero-tile">
                  <span className="bot-access-page__hero-tile-label">
                    <span className="bot-access-page__status-dot bot-access-page__status-dot--blocked" />
                    {t('botAccess.statusBlocked')}
                    <CustomTooltip tooltipText="botAccess.tileBlockedHint" />
                  </span>
                  <span className="bot-access-page__hero-tile-value">{stats.blocked}</span>
                </div>
              </div>
            </header>
          )}

          {isError && (
            <div className="warning-box warning-box--error bot-access-page__error-banner">
              {t('errorLoadingDataRetry')}
              <button className="btn btn--sm btn--secondary" onClick={handleRetry} type="button">
                {t('retry')}
              </button>
            </div>
          )}

          <div className="stats__panel">
            <div className="page-box__title-action">
              <div className="page-box__title page-box__title--flex">
                {t('botAccess.aiBotCrawlRules')}
                <span className="bot-access-page__count">
                  {t('botAccess.botCount', {
                    visible: filteredAgents.length,
                    total: agents.length,
                  })}
                </span>
              </div>
              <div className="page-box__source-filter">
                <div className="input-search-box">
                  <InputText
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('botAccess.searchPlaceholder')}
                    className="input-search"
                  />
                </div>
              </div>
            </div>

            {agents.length === 0 && !isError ? (
              <EmptyPanel textKey="botAccess.noData" size="l" />
            ) : (
              <BotStatusTable data={filteredAgents} />
            )}
          </div>
        </div>
      </div>
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

export { BotAccessPage };
export default BotAccessPage;
